import { useContext, useMemo, useRef, useState } from 'react';

import { AgGridReact } from 'ag-grid-react';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { Fieldset } from 'primereact/fieldset';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Control, UseFormWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from '@/app/hooks';
import { addToast } from '@/app/slices/toast';
import {
  SitesDropdown,
  StorageLocationAutoComplete,
  WithLocalStorage,
} from '@/components';
import { AccountsCombobox } from '@/components/common/accounts-combobox';
import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FormField } from '@/pages/embryo-details/components/FormField';
import { useMergeValueIntoOptions } from '@/pages/embryo-details/hooks/useMergeValueIntoOptions';
import { PrimaryContext, queryClient } from '@/services';
import {
  EmbryoDetailDto,
  ImplantedEmbryoDto,
  useGetImplantedEmbryos,
} from '@/services/embryo-service';
import { embryoServiceApi } from '@/services/embryo-service/api';
import { useContextMenu } from '@/utils/context-menu';
import { runNewWorkflow, WORKFLOW } from '@/utils/workflow';

import { implantedEmbryoColumDefs } from '../../util/columns/implanted-embryos';
import { AddImplantedEmbryosDialog } from '../AddImplantedEmbryosDialog';
import { EditImplantedEmbryosDialog } from '../EditImplantedEmbryosDialog';
import { EMBRYO_STATUS, OUTSIDE_EMBRYO_IMPLANT_FIELDS } from './constants';
import { EmbryoFormFieldConfig } from './field-config';
import {
  clinicRanchOptions,
  implantStatusOptions,
  implantTypeOptions,
  methodOptions,
  rightLeftOptions,
  thawMethodOptions,
  useAccountOptions,
  useTechOptions,
  useYardOptions,
} from './form-options';
import { useEmbryoFieldPolicy, useEmbryoFieldsEnabled } from './full-inventory';

type EmbryoImplantTabProps = {
  control: Control<EmbryoDetailDto>;
  watch: UseFormWatch<EmbryoDetailDto>;
  isBulkMode: boolean;
  hasManagerWrite?: boolean;
  columns?: number;
  isOutsideEmbryo?: boolean;
  outsideEmbryoId?: string;
};

export function EmbryoImplantTab({
  control,
  watch,
  isBulkMode: _isBulkMode,
  hasManagerWrite = false,
  columns = 3,
  isOutsideEmbryo = false,
  outsideEmbryoId,
}: EmbryoImplantTabProps) {
  const dispatch = useAppDispatch();
  const defaultGridOptions = useDefaultGridOptions();
  const { t } = useTranslation();
  const isOutsideEmbryoUpdatesEnabled = useFeatureFlag(
    'Outside_Embryo_Updates',
  );
  const isCreateEmbryoButtonRemoved = useFeatureFlag(
    'Remove_Create_Embryo_Button',
  );
  const isEmbryoRecipOwnerFieldEnabled = useFeatureFlag(
    'Embryo_Recip_Owner_Field',
  );
  const { themeMode } = useContext(PrimaryContext);
  const gridRef = useRef<AgGridReact>(null);
  const { getContextMenuItems } = useContextMenu({ idKey: 'id' });
  const [selectedRows, setSelectedRows] = useState<ImplantedEmbryoDto[]>([]);
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [addImplantsDialogVisible, setAddImplantsDialogVisible] =
    useState(false);
  const watchAny = watch as (name: string) => any;
  const { data: implantedResult, isLoading: _isImplantedLoading } =
    useGetImplantedEmbryos(outsideEmbryoId);

  const status = watch('status');
  const _implantDate = watch('implantDate');
  const techsOptions = useTechOptions();
  const accountOptions = useAccountOptions();
  const addRemoveEmbryoFields = useEmbryoFieldsEnabled();
  const isAccountComboboxEnabled = useFeatureFlag(
    'Account_Combobox_not_Dropdown',
  );
  const policy = useEmbryoFieldPolicy({ isOutsideEmbryo });

  const yardOptions = useYardOptions();

  const recipOwnerOptions = useMergeValueIntoOptions({
    currentOption: {
      label: watch('recipOwnerName') ?? '',
      value: watch('recipOwnerId') ?? '',
    },
    options: accountOptions,
  });

  const transferOwnerOptions = useMergeValueIntoOptions({
    currentOption: {
      label: watchAny('transferOwnerName') ?? '',
      value: watchAny('transferOwnerId') ?? '',
    },
    options: accountOptions,
  });

  const recipientOwnerOptions = useMergeValueIntoOptions({
    currentOption: {
      label: watchAny('recipientOwnerName') ?? '',
      value: watchAny('recipientOwnerId') ?? '',
    },
    options: accountOptions,
  });

  const implantBillingAccountOptions = useMergeValueIntoOptions({
    currentOption: {
      label: watchAny('implantBillingAccountName') ?? '',
      value: watchAny('implantBillingAccountId') ?? '',
    },
    options: accountOptions,
  });

  const deyardOptions = useMergeValueIntoOptions({
    currentOption: {
      label: watchAny('deyardName') ?? '',
      value: watchAny('deyardId') ?? '',
    },
    options: yardOptions,
  });

  const isRequired = status === EMBRYO_STATUS.IMPLANTED;

  const fieldConfigs: EmbryoFormFieldConfig[] = useMemo(
    () => [
      {
        name: 'calfType',
        label: t('transfer.implantType'),
        component: Dropdown,
        required: isRequired,
        componentProps: {
          options: implantTypeOptions,
          placeholder: 'Select Implant Type',
        },
      },
      {
        name: 'implantStatusCode',
        label: t('transfer.implantStatus'),
        component: Dropdown,
        componentProps: {
          options: implantStatusOptions,
          placeholder: 'Implant Status',
        },
      },
      {
        name: 'implantDate',
        label: t('transfer.implantDate'),
        component: Calendar,
        required: isRequired,
        componentProps: { dateFormat: 'mm/dd/yy' },
      },
      {
        name: 'recipOwnerId',
        label: t('embryo.recipOwner'),
        component: isAccountComboboxEnabled ? AccountsCombobox : Dropdown,
        ...(!isAccountComboboxEnabled && {
          lockedDisplayField: 'recipOwnerName',
        }),
        componentProps: isAccountComboboxEnabled
          ? {}
          : {
              options: recipOwnerOptions,
              placeholder: t('common.selectOption'),
            },
      },
      {
        name: 'implantBillingAccountId',
        label: t('transfer.implantBillingAccount'),
        component: isAccountComboboxEnabled ? AccountsCombobox : Dropdown,
        ...(!isAccountComboboxEnabled && {
          lockedDisplayField: 'implantBillingAccountName',
        }),
        componentProps: isAccountComboboxEnabled
          ? {}
          : {
              options: implantBillingAccountOptions,
              placeholder: t('common.selectOption'),
              filter: true,
              filterBy: 'label',
              showClear: true,
            },
      },
      {
        name: 'gnrhUsedAtImplant',
        label: t('transfer.gnrhUsed'),
        component: Checkbox,
      },
      {
        name: 'qual',
        label: t('transfer.transQuality'),
        component: InputNumber,
      },
      { name: 'dist', label: t('transfer.transDist'), component: InputText },
      {
        name: 'rightLeft',
        label: t('embryo.rightLeft'),
        component: Dropdown,
        componentProps: {
          options: rightLeftOptions,
          placeholder: t('common.select'),
        },
      },
      {
        name: 'technician1Id',
        label: t('transfer.transferTech'),
        component: Dropdown,
        componentProps: {
          options: techsOptions,
          placeholder: t('transfer.selectTech'),
        },
        required: isRequired,
      },
      {
        name: 'method',
        label: t('embryo.method'),
        component: Dropdown,
        requiresManagerWrite: true,
        componentProps: {
          options: methodOptions,
          placeholder: t('common.selectOption'),
        },
      },
      {
        name: isOutsideEmbryo ? 'thawByName' : 'thawById',
        label: t('transfer.thawBy'),
        component: isOutsideEmbryo ? InputText : Dropdown,
        ...(isOutsideEmbryo
          ? {}
          : {
              componentProps: {
                options: techsOptions,
                placeholder: t('transfer.selectTech'),
              },
            }),
      },
      {
        name: 'thawMethodId',
        label: t('transfer.thawMethod'),
        component: Dropdown,
        componentProps: {
          options: thawMethodOptions,
          placeholder: t('common.select'),
        },
      },
      {
        name: 'clinicRanchHaulIn',
        label: t('embryo.clinicRanchHaulin'),
        component: Dropdown,
        required: addRemoveEmbryoFields ? true : isRequired,
        componentProps: {
          options: clinicRanchOptions,
          placeholder: t('common.select'),
        },
      },
      ...(!addRemoveEmbryoFields
        ? [{ name: 'abort', label: t('transfer.abort'), component: Checkbox }]
        : []),
      ...(!addRemoveEmbryoFields
        ? [
            {
              name: 'lowQuality',
              label: t('transfer.lowQuality'),
              component: Checkbox,
            },
          ]
        : []),
      {
        name: isOutsideEmbryo ? 'transferSiteId' : 'siteId',
        label: isOutsideEmbryo ? t('site.transferSite') : t('site.siteId'),
        component: SitesDropdown,
        required: addRemoveEmbryoFields
          ? isOutsideEmbryo
            ? isRequired
            : true
          : isRequired,
        componentProps: {
          placeholder: t('site.selectSite'),
        },
      },
      ...(!addRemoveEmbryoFields
        ? [
            {
              name: 'additionalEmbryoImplant',
              label: t('transfer.additionalEmbryo'),
              component: Checkbox,
            },
          ]
        : []),
      {
        name: 'stage',
        label: t('embryo.stage'),
        component: InputNumber,
      },
      { name: 'grade', label: t('embryo.grade'), component: InputNumber },
      {
        name: 'transferQuantity',
        label: t('transfer.transferQuantity'),
        component: InputNumber,
        outsideOnly: true,
      },
      {
        name: 'implantTierOf',
        label: t('transfer.implantTierOf'),
        component: InputNumber,
        outsideOnly: true,
      },
      {
        name: 'deyardId',
        label: t('transfer.yard'),
        component: Dropdown,
        lockedDisplayField: 'deyardName',
        outsideOnly: true,
        componentProps: {
          options: deyardOptions,
          placeholder: t('common.selectOption'),
        },
      },
      {
        name: 'transferOwnerId',
        label: t('transfer.transferOwner'),
        component: isAccountComboboxEnabled ? AccountsCombobox : Dropdown,
        ...(!isAccountComboboxEnabled && {
          lockedDisplayField: 'transferOwnerName',
        }),
        outsideOnly: true,
        componentProps: isAccountComboboxEnabled
          ? {}
          : {
              options: transferOwnerOptions,
              placeholder: t('common.selectOption'),
            },
      },
      {
        name: 'recipientOwnerId',
        label: t('transfer.recipientOwner'),
        component: isAccountComboboxEnabled ? AccountsCombobox : Dropdown,
        ...(!isAccountComboboxEnabled && {
          lockedDisplayField: 'recipientOwnerName',
        }),
        outsideOnly: true,
        componentProps: isAccountComboboxEnabled
          ? {}
          : {
              options: recipientOwnerOptions,
              placeholder: t('common.selectOption'),
            },
      },
      {
        name: 'loadDonorPartners',
        label: t('transfer.loadDonorPartners'),
        component: Dropdown,
        outsideOnly: true,
        componentProps: {
          options: [
            { label: 'Yes', value: 1 },
            { label: 'No', value: 0 },
          ],
          placeholder: t('common.select'),
        },
      },
      ...(!addRemoveEmbryoFields
        ? [
            {
              name: 'clientRecipient',
              label: t('transfer.recipTag'),
              component: InputText,
              required: isRequired,
            },
            {
              name: 'preBook',
              label: t('transfer.prebookedImplant'),
              component: Checkbox,
            },
          ]
        : [
            {
              name: 'clientRecipient',
              label: t('transfer.recipTag'),
              component: InputText,
            },
          ]),
      {
        name: 'transferFarmLocationId',
        label: t('transfer.transferFarmLocation'),
        component: StorageLocationAutoComplete,
        componentProps: {
          field: 'name',
          placeholder: t('common.selectOption'),
          defaultValue:
            watchAny('transferFarmLocationId') &&
            watchAny('transferFarmLocationName')
              ? {
                  id: watchAny('transferFarmLocationId'),
                  name: watchAny('transferFarmLocationName'),
                }
              : null,
        },
      },
      {
        name: 'grader7Id',
        label: t('grading.grader'),
        component: Dropdown,
        componentProps: {
          options: techsOptions,
          placeholder: t('grading.selectGrader'),
        },
      },
      {
        name: 'loadById',
        label: t('transfer.loadBy'),
        component: Dropdown,
        componentProps: {
          options: techsOptions,
          placeholder: t('transfer.selectTech'),
        },
      },
      {
        name: 'creation',
        label: t('common.createdOn'),
        component: Calendar,
        componentProps: { dateFormat: 'mm/dd/yy' },
      },
      ...(!addRemoveEmbryoFields
        ? [
            {
              name: 'heat1',
              label: t('transfer.firstHeat'),
              component: Calendar,
              componentProps: { dateFormat: 'mm/dd/yy' },
            },
            {
              name: 'proj',
              label: t('transfer.projectCode'),
              component: InputText,
            },
            {
              name: 'projd',
              label: t('transfer.projectDesc'),
              component: InputText,
            },
          ]
        : [
            {
              name: 'heat1',
              label: t('transfer.firstHeat'),
              component: Calendar,
              componentProps: { dateFormat: 'mm/dd/yy' },
            },
          ]),
      {
        name: 'comm',
        label: t('transfer.implantComment'),
        component: InputTextarea,
        fullWidth: true,
        componentProps: { rows: 3 },
      },
      ...(addRemoveEmbryoFields
        ? ([
            {
              name: 'sync',
              label: t('embryo.sync'),
              component: InputText,
            },
            {
              name: 'loadTime',
              label: t('transfer.loadTime'),
              component: InputText,
            },
            {
              name: 'transferTime',
              label: t('transfer.transferTime'),
              component: InputText,
            },
            {
              name: 'impId',
              label: t('embryo.impId'),
              component: InputNumber,
              disabled: true,
            },
          ] satisfies EmbryoFormFieldConfig[])
        : []),
    ],
    [
      t,
      isRequired,
      isOutsideEmbryo,
      addRemoveEmbryoFields,
      isAccountComboboxEnabled,
      techsOptions,
      recipOwnerOptions,
      implantBillingAccountOptions,
      deyardOptions,
      transferOwnerOptions,
      recipientOwnerOptions,
      watchAny,
    ],
  );

  const lockedFields =
    isEmbryoRecipOwnerFieldEnabled || addRemoveEmbryoFields
      ? ['creation', 'transferFarmLocationId']
      : ['creation', 'recipOwnerId', 'transferFarmLocationId'];

  const visibleFields = (
    isOutsideEmbryo
      ? fieldConfigs.filter((f) =>
          (OUTSIDE_EMBRYO_IMPLANT_FIELDS as readonly string[]).includes(f.name),
        )
      : fieldConfigs.filter((f) => !f.outsideOnly)
  ).filter((f) => policy.isVisible(f.name));

  const handleAddImplantsConfirm = async (
    entries: { count: number; stage?: number; grade?: number }[],
  ) => {
    setAddImplantsDialogVisible(false);

    if (!outsideEmbryoId) {
      return;
    }

    try {
      const res = await embryoServiceApi.addImplantedEmbryosV2({
        outsideEmbryoId,
        entries,
      });

      addToast({
        severity: 'success',
        summary: 'Implants Added',
        detail: 'Implants successfully added to outside embryo.',
      });

      if (isOutsideEmbryoUpdatesEnabled) {
        const implantIds: string[] = res?.createdImplantEntryIds ?? [];
        if (implantIds.length > 0) {
          runNewWorkflow(
            implantIds.map((id: string) => ({ RecordId: id })),
            WORKFLOW.CreateEmbryoImplant.id,
          )
            .then(() => {
              setTimeout(() => {
                queryClient.invalidateQueries({
                  queryKey: ['embryo', 'implanted', outsideEmbryoId],
                });
              }, 7000);
            })
            .catch(() => {
              addToast({
                severity: 'warn',
                summary: 'Workflow Warning',
                detail:
                  'Implants added but Create Embryo workflow failed to start.',
              });
            });
        }
      }

      await queryClient.invalidateQueries({
        queryKey: ['embryo', 'implanted', outsideEmbryoId],
      });
    } catch (error) {
      addToast({
        severity: 'error',
        summary: 'Error',
        detail:
          error instanceof Error ? error.message : 'Failed to add implants.',
      });
    }
  };

  const handleCreateEmbryo = async () => {
    const missingRecipTag = selectedRows.some((row) => !row.recipient);
    if (missingRecipTag) {
      dispatch(
        addToast({
          severity: 'warn',
          summary: 'Validation',
          detail: 'Recip Tag is required.',
        }),
      );
      return;
    }

    const recordIds = selectedRows.map((row) => ({ RecordId: row.id }));

    try {
      await runNewWorkflow(recordIds, WORKFLOW.CreateEmbryoImplant.id);

      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ['embryo', 'implanted', outsideEmbryoId],
        });
      }, 7000);

      dispatch(
        addToast({
          severity: 'success',
          summary: 'Workflow Executed',
          detail:
            'The Create Embryo workflow has been started. The grid will refresh automatically in a few seconds.',
          life: ToastLife.LONG,
        }),
      );
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Workflow Error',
          detail: `Failed to execute Create Embryo workflow. Please try again. Error: ${errorMessage}`,
          life: ToastLife.LONG,
        }),
      );
    }
  };

  return (
    <div className="implant-tog-frozen-embryo-tab">
      {!isOutsideEmbryo && (
        <Fieldset
          pt={{
            root: { className: 'p-2' },
            content: { className: 'space-y-2' },
          }}
        >
          <div
            className="grid gap-x-6 gap-y-1"
            style={{
              gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
            }}
          >
            {visibleFields.map((field) => (
              <FormField
                key={field.name}
                label={field.label}
                name={field.name}
                control={control}
                component={field.component}
                disabled={
                  field.disabled ||
                  lockedFields.includes(field.name) ||
                  policy.isLocked(field.name) ||
                  (field.requiresManagerWrite === true && !hasManagerWrite)
                }
                required={policy.isRequired(field.name, field.required)}
                lockedDisplayField={field.lockedDisplayField}
                componentProps={field.componentProps}
              />
            ))}
          </div>
        </Fieldset>
      )}
      {isOutsideEmbryo && outsideEmbryoId && (
        <div className="mt-4">
          <div className="mb-2 flex justify-end gap-2">
            <>
              {!isCreateEmbryoButtonRemoved && (
                <Button
                  label="Create Embryo"
                  size="small"
                  disabled={selectedRows.length === 0}
                  onClick={handleCreateEmbryo}
                  aria-label="Create Embryo"
                />
              )}
              <Button
                label="Refresh"
                size="small"
                icon="pi pi-refresh"
                onClick={() =>
                  queryClient.invalidateQueries({
                    queryKey: ['embryo', 'implanted', outsideEmbryoId],
                  })
                }
                aria-label="Refresh Grid"
              />
            </>
            <Button
              label="Add Implanted Embryos"
              size="small"
              onClick={() => setAddImplantsDialogVisible(true)}
            />
            <Button
              label="Edit Selected"
              size="small"
              disabled={selectedRows.length === 0}
              onClick={() => setEditDialogVisible(true)}
              tooltip={
                isOutsideEmbryoUpdatesEnabled
                  ? 'Editing will also update the associated live embryo'
                  : undefined
              }
              tooltipOptions={{ position: 'top' }}
            />
          </div>
          <div className="ag-grid-height-300">
            <WithLocalStorage storageKey="embryo-implanted-grid">
              <AgGridReact
                gridOptions={defaultGridOptions}
                ref={gridRef}
                className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''}`}
                defaultColDef={defaultColDef}
                rowData={implantedResult?.items ?? []}
                columnDefs={implantedEmbryoColumDefs}
                rowSelection="multiple"
                onSelectionChanged={() =>
                  setSelectedRows(gridRef.current!.api.getSelectedRows())
                }
                getContextMenuItems={getContextMenuItems}
              />
            </WithLocalStorage>
          </div>
        </div>
      )}
      <EditImplantedEmbryosDialog
        visible={editDialogVisible}
        selectedRows={selectedRows}
        onHide={() => setEditDialogVisible(false)}
        showEmbryoUpdateNote={isOutsideEmbryoUpdatesEnabled}
      />
      <AddImplantedEmbryosDialog
        visible={addImplantsDialogVisible}
        onHide={() => setAddImplantsDialogVisible(false)}
        onConfirm={(entries) => {
          setAddImplantsDialogVisible(false);
          handleAddImplantsConfirm(entries);
        }}
      />
    </div>
  );
}
